/**
 * React Query hooks for in-app notifications.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotifications,
  getNotificationStats,
  getRecentNotifications,
  markAsRead,
  markAllAsRead,
  dismissNotification,
  dismissAllNotifications,
} from '@/services/notification-api';
import type { NotificationListFilters } from '@/types/notification';

const KEYS = {
  list: (filters: NotificationListFilters) => ['notifications', filters] as const,
  stats: ['notification-stats'] as const,
  recent: ['notifications-recent'] as const,
};

export function useNotifications(filters: NotificationListFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => getNotifications(filters),
    refetchInterval: 30_000, // poll every 30s
  });
}

export function useNotificationStats() {
  return useQuery({
    queryKey: KEYS.stats,
    queryFn: getNotificationStats,
    refetchInterval: 15_000, // poll every 15s for badge count
  });
}

export function useRecentNotifications() {
  return useQuery({
    queryKey: KEYS.recent,
    queryFn: getRecentNotifications,
    refetchInterval: 10_000, // poll every 10s for toasts
  });
}

export function useMarkAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: KEYS.stats });
      qc.invalidateQueries({ queryKey: KEYS.recent });
    },
  });
}

export function useMarkAllAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: KEYS.stats });
      qc.invalidateQueries({ queryKey: KEYS.recent });
    },
  });
}

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: dismissNotification,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: KEYS.stats });
      qc.invalidateQueries({ queryKey: KEYS.recent });
    },
  });
}

export function useDismissAllNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: dismissAllNotifications,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: KEYS.stats });
      qc.invalidateQueries({ queryKey: KEYS.recent });
    },
  });
}
